import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";

import styles from "./Composer.module.css";

const MAX_CHARS = 8000;

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
}

export function Composer({ onSend, disabled }: Props) {
  const [value, setValue] = useState("");
  // What the customer typed and sent WHILE a reply was still arriving.
  //
  // Pressing Enter then did nothing at all: submit() returned early, the text
  // sat in the box, and there was no indicator of any kind — reported as
  // "messages sent while it's replying are dropped". Held here and sent the
  // moment the reply finishes, so nothing a customer typed is ever thrown
  // away without them being told.
  const [queued, setQueued] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!disabled && queued) {
      const text = queued;
      setQueued(null);
      onSend(text);
    }
  }, [disabled, queued, onSend]);

  function grow() {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 110)}px`;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const text = value.trim();
    if (!text) return;
    setValue("");
    requestAnimationFrame(grow);
    if (disabled) {
      setQueued(text);
      return;
    }
    onSend(text);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit(event);
    }
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      <textarea
        ref={ref}
        className={styles.input}
        value={value}
        onChange={(e) => {
          setValue(e.target.value.slice(0, MAX_CHARS));
          grow();
        }}
        onKeyDown={onKeyDown}
        placeholder={
          queued
            ? "Sending once the reply finishes…"
            : "Ask about a game, app, model, budget…"
        }
        rows={1}
        aria-label="Message"
      />
      {value.length >= MAX_CHARS - 500 && (
        // The cap used to be silent: typing simply stopped having an effect
        // at 8,000 characters, with nothing to say why. QA reported it as
        // "no input length limit or warning" — the limit was there, the
        // warning was not. Only shown near the end, so it never nags.
        <span
          className={styles.counter}
          role="status"
          aria-live="polite"
        >
          {value.length >= MAX_CHARS
            ? `That's the ${MAX_CHARS.toLocaleString()} character limit — put the important part at the end.`
            : `${(MAX_CHARS - value.length).toLocaleString()} characters left`}
        </span>
      )}
      <button
        type="submit"
        className={styles.send}
        disabled={value.trim().length === 0}
        aria-label="Send message"
      >
        <span className={styles.arrow} />
      </button>
    </form>
  );
}
